import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { adminApi, publicApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { formatCurrency } from '../../config'
import type { Stats, Event, RegistrationModeResponse, SponsorshipStats } from '../../types'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import SilentAuctionModal from '../../components/common/SilentAuctionModal'

interface PaidGolfer {
  name: string
  email: string
  source: 'registration' | 'sponsor_foursome'
  teamName?: string
  sponsorName?: string
}

export default function AdminDashboard() {
  const { getToken } = useAuth()
  const [stats, setStats] = useState<Stats | null>(null)
  const [sponsorshipStats, setSponsorshipStats] = useState<SponsorshipStats | null>(null)
  const [events, setEvents] = useState<Event[]>([])
  const [registrationMode, setRegistrationMode] = useState<RegistrationModeResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [togglingBypass, setTogglingBypass] = useState(false)
  const [togglingForceWaitlist, setTogglingForceWaitlist] = useState(false)
  const [activeEventId, setActiveEventId] = useState<string | null>(null)

  // Silent Auction modal state
  const [auctionModalOpen, setAuctionModalOpen] = useState(false)
  const [golfers, setGolfers] = useState<PaidGolfer[]>([])
  const [loadingGolfers, setLoadingGolfers] = useState(false)

  useEffect(() => {
    async function fetchData() {
      try {
        const token = await getToken()
        if (!token) throw new Error('Not authenticated')

        const [statsData, eventsData, sponsorshipStatsData] = await Promise.all([
          adminApi.getStats(token),
          adminApi.getEvents(token),
          adminApi.getSponsorshipStats(token),
        ])

        setStats(statsData)
        setEvents(eventsData.events)
        setSponsorshipStats(sponsorshipStatsData)

        // Fetch registration mode for the first active event
        const activeEvent = eventsData.events.find((e: Event) => e.active)
        if (activeEvent) {
          setActiveEventId(activeEvent.eventId)
          if (activeEvent.slug) {
            try {
              const modeData = await publicApi.getRegistrationState(activeEvent.slug)
              setRegistrationMode(modeData)
            } catch {
              // Ignore registration mode errors
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [getToken])

  const refreshRegistrationMode = async () => {
    const activeEvent = events.find((e) => e.active)
    if (activeEvent?.slug) {
      const modeData = await publicApi.getRegistrationState(activeEvent.slug)
      setRegistrationMode(modeData)
    }
  }

  const handleToggleBypass = async () => {
    if (!activeEventId) return

    const newValue = !registrationMode?.waitlistBypassEnabled
    setTogglingBypass(true)

    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      await adminApi.toggleWaitlistBypass(token, activeEventId, newValue)
      await refreshRegistrationMode()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle bypass')
    } finally {
      setTogglingBypass(false)
    }
  }

  const handleToggleForceWaitlist = async () => {
    if (!activeEventId) return

    const newValue = !registrationMode?.waitlistForced
    setTogglingForceWaitlist(true)

    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      await adminApi.toggleWaitlistMode(token, activeEventId, newValue)
      await refreshRegistrationMode()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle waitlist mode')
    } finally {
      setTogglingForceWaitlist(false)
    }
  }

  const handleOpenAuctionModal = async () => {
    setAuctionModalOpen(true)
    setGolfers([])
    setLoadingGolfers(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const data = await adminApi.getPaidGolfers(token)
      setGolfers(data.golfers)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load golfers')
      setAuctionModalOpen(false)
    } finally {
      setLoadingGolfers(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn btn-primary"
        >
          Try Again
        </button>
      </div>
    )
  }

  // Calculate combined totals
  const registrationRevenue = stats?.totalFundsCents || 0
  const sponsorshipRevenue = sponsorshipStats?.totalRevenueCents || 0
  const totalFundsRaised = registrationRevenue + sponsorshipRevenue

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Dashboard</h1>

      {/* Total Funds Raised - Highlighted */}
      <div className="mb-6">
        <div className="rounded-lg border-2 border-green-300 bg-green-50 p-6 text-center">
          <p className="text-sm text-green-600 font-medium mb-1">Total Funds Raised (gross)</p>
          <p className="text-4xl font-bold text-green-700">{formatCurrency(totalFundsRaised)}</p>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <StatCard
          label="Golf Registration Revenue"
          value={formatCurrency(registrationRevenue)}
          color="blue"
        />
        <StatCard
          label="Sponsorship Revenue + Golf Foursome"
          value={formatCurrency(sponsorshipRevenue)}
          color="purple"
        />
      </div>

      {/* Golfers and Sponsors */}
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        {/* Golfers - with breakdown */}
        <div className="rounded-lg border p-4 bg-indigo-50 border-indigo-200 text-indigo-700">
          <p className="text-sm opacity-75">Total Golfers</p>
          <p className="text-2xl font-bold">
            {(stats?.totalGolfers || 0) + (sponsorshipStats?.foursomeGolferCount || 0)}
          </p>
          <div className="mt-2 pt-2 border-t border-indigo-200 text-xs space-y-1">
            <div className="flex justify-between">
              <span>Registrations:</span>
              <span className="font-medium">{stats?.totalGolfers || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Sponsorship Foursomes:</span>
              <span className="font-medium">{sponsorshipStats?.foursomeGolferCount || 0}</span>
            </div>
          </div>
        </div>
        <StatCard
          label="Sponsors"
          value={sponsorshipStats?.totalSponsors?.toString() || '0'}
          color="orange"
        />
      </div>

      {/* Waitlist indicator if active */}
      {registrationMode?.mode === 'waitlist' && registrationMode.capacity?.waitlistRegistrations > 0 && (
        <div className="grid sm:grid-cols-1 gap-4 mb-8">
          <StatCard
            label="On Waitlist"
            value={registrationMode.capacity.waitlistRegistrations.toString()}
            color="cyan"
          />
        </div>
      )}

      {/* Registration Mode Control */}
      {registrationMode && activeEventId && (
        <div className="card mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Registration Mode
          </h2>

          {/* Current mode and capacity */}
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-1">
              Current Mode:{' '}
              <span className={`font-medium ${
                registrationMode.mode === 'normal' ? 'text-green-600' :
                registrationMode.mode === 'waitlist' ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {registrationMode.mode === 'normal' ? 'Open Registration' :
                 registrationMode.mode === 'waitlist' ? 'Waitlist Mode' :
                 'Closed'}
              </span>
            </p>
            {registrationMode.capacity && (
              <p className="text-sm text-gray-500">
                <span className="font-semibold text-gray-700">
                  {registrationMode.capacity.paidGolfers} / {registrationMode.capacity.totalCapacity} golfers registered
                </span>
                {' '}({stats?.totalTeams || 0} teams)
                {registrationMode.capacity.waitlistRegistrations > 0 &&
                  ` | ${registrationMode.capacity.waitlistRegistrations} on waitlist`
                }
              </p>
            )}
          </div>

          {/* Toggle controls */}
          <div className="space-y-3 border-t pt-4">
            {/* Force Waitlist Mode toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Force Waitlist Mode</p>
                <p className="text-xs text-gray-500">Manually put registration into waitlist mode regardless of capacity</p>
              </div>
              <button
                onClick={handleToggleForceWaitlist}
                disabled={togglingForceWaitlist}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  registrationMode.waitlistForced
                    ? 'bg-yellow-500'
                    : 'bg-gray-300'
                } ${togglingForceWaitlist ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    registrationMode.waitlistForced
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Waitlist Bypass toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Waitlist Bypass</p>
                <p className="text-xs text-gray-500">Keep normal registration open even when capacity is full or waitlist is forced</p>
              </div>
              <button
                onClick={handleToggleBypass}
                disabled={togglingBypass}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  registrationMode.waitlistBypassEnabled
                    ? 'bg-green-500'
                    : 'bg-gray-300'
                } ${togglingBypass ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    registrationMode.waitlistBypassEnabled
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Status warnings */}
          {registrationMode.waitlistForced && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Waitlist Forced:</strong> Registration is manually set to waitlist mode. New registrants will join the waitlist instead of paying.
              </p>
            </div>
          )}
          {registrationMode.waitlistBypassEnabled && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Bypass Active:</strong> Normal registration remains open regardless of capacity limits or forced waitlist. New registrants go directly to payment.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="card mb-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Quick Actions
        </h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/registrations" className="btn btn-primary">
            View All Registrations
          </Link>
          <Link to="/admin/teams" className="btn btn-secondary">
            Manage Teams
          </Link>
          <Link to="/admin/sponsors" className="btn btn-secondary">
            Manage Sponsors
          </Link>
          <button
            onClick={handleOpenAuctionModal}
            className="btn bg-amber-600 hover:bg-amber-700 text-white"
          >
            Send Silent Auction Email
          </button>
        </div>
      </div>

      {/* Events */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Events</h2>

        {events.length === 0 ? (
          <p className="text-gray-500">No events found.</p>
        ) : (
          <div className="divide-y">
            {events.map((event) => (
              <div
                key={event.eventId}
                className="py-3 flex justify-between items-center"
              >
                <div>
                  <h3 className="font-medium text-gray-800">{event.name}</h3>
                  <p className="text-sm text-gray-500">/{event.slug}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    event.active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {event.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Last Updated */}
      {stats?.updatedAt && (
        <p className="text-xs text-gray-400 mt-4 text-right">
          Last updated: {new Date(stats.updatedAt).toLocaleString()}
        </p>
      )}

      {/* Silent Auction Email Modal */}
      {auctionModalOpen && (
        <SilentAuctionModal
          golfers={golfers}
          loadingGolfers={loadingGolfers}
          onClose={() => setAuctionModalOpen(false)}
        />
      )}
    </div>
  )
}

interface StatCardProps {
  label: string
  value: string
  color: 'green' | 'blue' | 'indigo' | 'purple' | 'cyan' | 'orange'
}

function StatCard({ label, value, color }: StatCardProps) {
  const colorClasses = {
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    cyan: 'bg-cyan-50 border-cyan-200 text-cyan-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
  }

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <p className="text-sm opacity-75">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  )
}
